import { createFileRoute } from '@tanstack/react-router';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import { motion, type Variants } from 'framer-motion';
import {
  ArrowRight,
  Award,
  Compass,
  Mail,
  Play,
  Radio,
  Ship,
  Users,
  Waves,
} from 'lucide-react';
import { LandingTopBar } from '@/featured/landing-page/components/LandingTopBar';
import { LandingFooter } from '@/featured/landing-page/components/LandingFooter';
import ModalVideo from 'react-modal-video';
import 'react-modal-video/css/modal-video.css';
const VideoModal: any = (ModalVideo as any)?.default || ModalVideo;

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

function AboutPage() {
  useEffect(() => {
    document.documentElement.setAttribute('dir', 'ltr');
  }, []);

  return (
    <>
      <Head>
        <title>About — Missi Track</title>
        <meta name="description" content="Missi Track helps maritime operators manage and track vessels efficiently with seamless real-time fleet visibility and support." />
        <meta property="og:title" content="About — Missi Track" />
        <meta property="og:description" content="Where we assist you in efficiently managing and tracking your vessels across global maritime operations." />
        <link href="https://fonts.googleapis.com/css2?family=Archivo+Black&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </Head>
      <main className="landing min-h-screen">
        <LandingTopBar />
        <Hero />
        <WhoWeAre />
        <Values />
        <TeamCTA />
        <LandingFooter />
      </main>
    </>
  );
}

AboutPage.guestGuard = false;
AboutPage.authGuard = false;

export default AboutPage;

/* ---------------- Hero ---------------- */
function Hero() {
  return (
    <section className="px-4 pt-24 md:pt-32 pb-6">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial="hidden"
          animate="show"
          variants={stagger}
          className="relative overflow-hidden rounded-[2rem] bg-ink p-8 text-white md:p-14"
          style={{
            backgroundImage: `linear-gradient(135deg, oklch(0.18 0.02 240 / 0.85), oklch(0.10 0.02 240 / 0.92)), url('/images/jumbotron.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <motion.div variants={fadeUp}>
            <span className="pill bg-white/10 text-white shadow-none">
              ABOUT US
            </span>
          </motion.div>
          <motion.h1
            variants={fadeUp}
            className="display mt-6 text-[12vw] font-black leading-[0.85] tracking-tight md:text-[8rem]"
          >
            ABOUT US
          </motion.h1>
          <motion.p
            variants={fadeUp}
            className="mt-6 max-w-2xl text-base text-white/70 md:text-lg"
          >
            Where we assist you in efficiently managing and tracking your vessels. Our mission is to provide seamless support for your maritime operations, ensuring optimal control and oversight of your fleet.
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="mt-10 grid gap-4 sm:grid-cols-3"
          >
            {[
              { k: '600++', l: 'Tracking Active' },
              { k: '50++', l: 'Companies' },
              { k: '99%', l: 'Uptime' },
            ].map((s) => (
              <div
                key={s.k}
                className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur"
              >
                <div className="text-3xl font-black md:text-4xl">{s.k}</div>
                <div className="mt-1 text-xs uppercase tracking-wider text-white/60">
                  {s.l}
                </div>
              </div>
            ))}
          </motion.div>

          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary/30 blur-3xl" />
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Who we are ---------------- */
function WhoWeAre() {
  return (
    <section className="px-4 py-20 md:py-28 bg-background">
      <div className="mx-auto grid max-w-7xl items-center gap-10 md:grid-cols-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
          className="grid grid-cols-2 gap-4 md:col-span-5"
        >
          <motion.img
            variants={fadeUp}
            src="/images/ab01.jpg"
            alt="Fleet at sea"
            width={800}
            height={1000}
            loading="lazy"
            className="h-full w-full rounded-2xl object-cover shadow-[0_8px_30px_-12px_rgba(0,0,0,0.5)]"
          />
          <motion.div variants={fadeUp} className="flex flex-col gap-4">
            <img
              src="/images/ab03.jpg"
              alt="Vessel side view"
              width={800}
              height={500}
              loading="lazy"
              className="h-[48%] w-full rounded-2xl object-cover shadow-[0_8px_30px_-12px_rgba(0,0,0,0.5)]"
            />
            <img
              src="/images/ab02.jpg"
              alt="Operations center"
              width={800}
              height={500}
              loading="lazy"
              className="h-[48%] w-full rounded-2xl object-cover shadow-[0_8px_30px_-12px_rgba(0,0,0,0.5)]"
            />
          </motion.div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
          className="md:col-span-7 md:pl-6"
        >
          <motion.div variants={fadeUp} className="flex items-baseline gap-3">
            <span className="text-5xl font-black text-primary md:text-6xl">
              7+
            </span>
            <span className="text-sm font-medium leading-tight text-foreground">
              Years
              <br />
              Experience
            </span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="display mt-6 text-4xl font-black leading-[0.95] md:text-6xl text-foreground"
          >
            WHO WE <span className="text-primary">ARE?</span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-5 max-w-xl text-muted-foreground"
          >
            The Maritime Sectors We Cater To — Embark on your journey with Missi Track Vessel Tracking, equipped with everything you need to raise visibility, enhance navigation, and establish seamless connections within the maritime industry. Let&apos;s navigate the waters together.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
            <a
              href="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
            >
              <Mail className="h-4 w-4" /> Contact us
            </a>
            <a
              href="/"
              className="inline-flex items-center gap-2 rounded-full bg-secondary px-5 py-3 text-sm font-semibold text-foreground transition hover:bg-foreground hover:text-background"
            >
              Explore services <ArrowRight className="h-4 w-4" />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Values ---------------- */
const VALUES = [
  {
    icon: Compass,
    title: 'Precision Navigation',
    text: 'Accurate GPS and AIS positioning so you always know where every vessel is.',
  },
  {
    icon: Radio,
    title: 'Always-On Communication',
    text: 'Iridium and Globalstar satellite links keep your fleet connected globally.',
  },
  {
    icon: Waves,
    title: 'Maritime Expertise',
    text: 'Built by maritime specialists for the realities of life at sea.',
  },
  {
    icon: Award,
    title: 'Award-Winning Service',
    text: 'Recognized for business excellence and customer-first support.',
  },
];

function Values() {
  return (
    <section className="px-4 pb-20 bg-background">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-10 flex flex-wrap items-end justify-between gap-4"
        >
          <h2 className="display text-4xl font-black md:text-6xl text-foreground">
            WHAT DRIVES <span className="text-primary">US</span>
          </h2>
          <p className="max-w-sm text-sm text-muted-foreground">
            Four commitments that shape every line of code and every device we
            deploy.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={stagger}
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
        >
          {VALUES.map((v) => (
            <motion.div
              key={v.title}
              variants={fadeUp}
              className="card-surface group flex h-full flex-col p-6 hover-lift"
            >
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-secondary text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
                <v.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-foreground">{v.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{v.text}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Team CTA ---------------- */
function TeamCTA() {
  const [isOpen, setOpen] = useState(false);
  return (
    <section className="px-4 pb-24 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem]"
      >
        <img
          src="/images/cta-bg.jpg"
          alt="Missi Track operations team"
          width={1920}
          height={1000}
          loading="lazy"
          className="h-[420px] w-full object-cover md:h-[520px]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-8 md:p-14">
          <div className="flex flex-wrap items-end justify-between gap-6 text-white">
            <div>
              <span className="pill bg-white/10 text-white shadow-none">
                TEAM
              </span>
              <h3 className="display mt-4 text-4xl font-black leading-tight md:text-6xl">
                MEET THE EXPERIENCED
                <br />
                <span className="text-primary">TEAM MEMBERS</span>
              </h3>
              <p className="mt-4 max-w-md text-sm text-white/70">
                A multidisciplinary crew of maritime engineers, satellite
                experts and software builders — all in one tide.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setOpen(true)}
                className="grid h-16 w-16 place-items-center rounded-full bg-primary text-primary-foreground transition hover:scale-105"
              >
                <Play className="h-6 w-6 translate-x-0.5 fill-current" />
              </button>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-foreground transition hover:bg-primary hover:text-primary-foreground"
              >
                <Users className="h-4 w-4" /> Join the crew
              </a>
            </div>
          </div>
        </div>
      </motion.div>
      {isOpen && typeof window !== 'undefined' && (
        <VideoModal
          channel="youtube"
          autoplay
          isOpen={isOpen}
          videoId="SKdPLZyIN0o"
          onClose={() => setOpen(false)}
        />
      )}
    </section>
  );
}

export const Route = createFileRoute('/about')({
  component: AboutPage,
});

